import { Phone } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/data-table';

import { getStatusBadgeVariant } from '@/lib/badge';
import { formatDate } from '@/lib/date';

import { RowAction } from './row-action';

import type { BatchCall } from '@/types/scheduler.type';
import type { ColumnDef } from '@tanstack/react-table';

export const getColumns = (): ColumnDef<BatchCall>[] => [
  {
    id: 'batchName',
    accessorKey: 'batchName',
    header: ({ column }) => <SortableHeader label="Batch name" column={column} />,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <Phone className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <span className="font-medium text-foreground">{row.original.batchName}</span>
      </div>
    ),
  },
  {
    id: 'agent',
    header: ({ column }) => <SortableHeader label="Agent" column={column} />,
    cell: ({ row }) => (
      <span>
        {row.original.agent.agentName} ({row.original.agent.phoneNumber})
      </span>
    ),
  },
  {
    id: 'totalRecipients',
    accessorKey: 'totalRecipients',
    header: 'Recipients',
    cell: ({ getValue }) => (
      <div className="flex items-center gap-2">
        <span className="font-medium">{getValue() as string}</span>
        <span className="text-muted-foreground text-sm">recipients</span>
      </div>
    ),
  },
  {
    id: 'scheduledTime',
    accessorKey: 'scheduledTime',
    header: ({ column }) => <SortableHeader label="Scheduled time" column={column} />,
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{formatDate(getValue() as string)}</span>
    ),
  },
  {
    id: 'timezone',
    accessorKey: 'timezone',
    header: ({ column }) => <SortableHeader label="Timezone" column={column} />,
    cell: ({ getValue }) => <span className="text-muted-foreground">{getValue() as string}</span>,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader label="Status" column={column} />,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      return <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>;
    },
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: ({ column }) => <SortableHeader label="Created at" column={column} />,
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{formatDate(getValue() as string)}</span>
    ),
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => <RowAction call={row.original} />,
  },
];
